/**
 * Shopping — Commerce Agent 购物决策页
 * 
 * 用户输入购物需求，Commerce Agent 完成跨平台比价、评价分析、购买建议
 * 分管分类：数码/服饰/家居/美妆/食品/运动
 * 
 * 设计风格：暗夜蓝 + 萤火暖金，与 LifePage 统一的设计语言
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

/* ═══════════════════════════════════════════
   类型定义
   ═══════════════════════════════════════════ */

interface Product {
  id: string;
  name: string;
  category: string;
  price: number;
  match: number;
  source: string;
  rating: number;
  reviews: number;
  image: string;
  tags: string[];
}

interface PriceRow {
  platform: string;
  price: number;
  shipping: string;
  warranty: string;
  url: string;
  best?: boolean;
}

interface ReviewKeyword {
  text: string;
  sentiment: 'positive' | 'neutral' | 'negative';
  count: number;
}

/* ═══════════════════════════════════════════
   模拟数据
   ═══════════════════════════════════════════ */

const MOCK_PRODUCTS: Product[] = [
  { id: 'p1', name: 'Keychron K8 Pro 红轴机械键盘', category: '数码', price: 568, match: 0.94, source: '京东', rating: 4.8, reviews: 12034, image: '⌨️', tags: ['红轴', '静音', '蓝牙', '热插拔'] },
  { id: 'p2', name: 'HHKB Professional Hybrid', category: '数码', price: 1980, match: 0.87, source: '天猫', rating: 4.9, reviews: 3421, image: '⌨️', tags: ['静电容', '程序员', '蓝牙', '静音'] },
  { id: 'p3', name: 'Leopold FC660M PD', category: '数码', price: 899, match: 0.91, source: '淘宝', rating: 4.7, reviews: 8762, image: '⌨️', tags: ['红轴', '66键', 'PBT键帽'] },
  { id: 'p4', name: '罗技 MX Keys Mini', category: '数码', price: 699, match: 0.85, source: '京东', rating: 4.6, reviews: 9871, image: '⌨️', tags: ['薄膜', '超薄', '多设备'] },
  { id: 'p5', name: 'Sony WH-1000XM5', category: '数码', price: 2299, match: 0.82, source: '天猫', rating: 4.8, reviews: 15234, image: '🎧', tags: ['降噪', '无线', 'Hi-Res'] },
  { id: 'p6', name: '优衣库 U 宽松牛仔夹克', category: '服饰', price: 399, match: 0.89, source: '天猫', rating: 4.5, reviews: 6789, image: '🧥', tags: ['牛仔', '百搭', '秋冬'] },
  { id: 'p7', name: 'MUJI 无印良品 香薰加湿器', category: '家居', price: 248, match: 0.92, source: '京东', rating: 4.7, reviews: 23456, image: '🏠', tags: ['静音', '大容量', '香薰'] },
  { id: 'p8', name: 'SK-II 神仙水 230ml', category: '美妆', price: 1590, match: 0.88, source: '天猫', rating: 4.8, reviews: 45123, image: '✨', tags: ['精华', '补水', '限量'] },
  { id: 'p9', name: '三顿半 超即溶咖啡 64颗', category: '食品', price: 189, match: 0.91, source: '淘宝', rating: 4.6, reviews: 34567, image: '☕', tags: ['速溶', '精品豆', '便携'] },
  { id: 'p10', name: 'Lululemon Align 瑜伽裤', category: '运动', price: 850, match: 0.86, source: '京东', rating: 4.7, reviews: 12890, image: '🧘', tags: ['裸感', '高腰', '透气'] },
];

const MOCK_PRICE_COMPARE: PriceRow[] = [
  { platform: '京东自营', price: 568, shipping: '免运费', warranty: '2年质保', url: '#', best: true },
  { platform: '天猫旗舰', price: 579, shipping: '免运费', warranty: '2年质保', url: '#' },
  { platform: '拼多多百亿补贴', price: 499, shipping: '到付', warranty: '1年质保', url: '#' },
  { platform: '亚马逊海外购', price: 528, shipping: '约30元', warranty: '1年', url: '#' },
  { platform: '抖音商城', price: 549, shipping: '免运费', warranty: '1年质保', url: '#' },
];

const MOCK_REVIEW_KEYWORDS: ReviewKeyword[] = [
  { text: '手感极佳', sentiment: 'positive', count: 8234 },
  { text: '静音效果好', sentiment: 'positive', count: 6512 },
  { text: '蓝牙稳定', sentiment: 'positive', count: 5891 },
  { text: '做工精良', sentiment: 'positive', count: 4567 },
  { text: '打字不累', sentiment: 'positive', count: 3891 },
  { text: '灯光炫酷', sentiment: 'neutral', count: 3245 },
  { text: '稍重', sentiment: 'neutral', count: 1234 },
  { text: '键帽易打油', sentiment: 'negative', count: 876 },
];

/* ═══════════════════════════════════════════
   工具函数
   ═══════════════════════════════════════════ */

const getTimeOfDay = () => {
  const h = new Date().getHours();
  if (h >= 5 && h < 12) return '上午好';
  if (h >= 12 && h < 18) return '下午好';
  if (h >= 18 && h < 22) return '晚上好';
  return '夜深了';
};

const getRecommendationReason = (product: Product): string[] => {
  const reasons: string[] = [];
  if (product.match >= 0.9) reasons.push('需求匹配度极高');
  if (product.rating >= 4.7) reasons.push('用户口碑优秀');
  if (product.reviews >= 10000) reasons.push('热销已验证');
  if (product.price <= 600) reasons.push('性价比突出');
  reasons.push(`评分 ${product.rating}·${product.reviews.toLocaleString()} 条评价`);
  return reasons.length > 0 ? reasons : ['综合评分较高'];
};

/* ═══════════════════════════════════════════
   子组件
   ═══════════════════════════════════════════ */

/** 商品卡片 */
function ProductCard({ product, index, onSelect }: { product: Product; index: number; onSelect: (p: Product) => void }) {
  return (
    <div
      onClick={() => onSelect(product)}
      style={{
        padding: '18px 20px', borderRadius: 16,
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.06)',
        cursor: 'pointer',
        transition: 'all 250ms var(--ease)',
        animation: `fadeUp 0.5s ${(0.1 + index * 0.06).toFixed(2)}s var(--ease) both`,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = 'rgba(232, 210, 138, 0.2)';
        e.currentTarget.style.background = 'rgba(232, 210, 138, 0.05)';
        e.currentTarget.style.transform = 'translateY(-2px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)';
        e.currentTarget.style.background = 'rgba(255,255,255,0.03)';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
        <div style={{ fontSize: 32, flexShrink: 0 }}>{product.image}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)', marginBottom: 6, lineHeight: 1.4 }}>
            {product.name}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <span style={{ fontSize: 16, fontWeight: 600, color: 'var(--accent)' }}>¥{product.price}</span>
            <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: 'rgba(232, 210, 138, 0.08)', color: 'var(--accent)', fontWeight: 500 }}>
              匹配度 {Math.round(product.match * 100)}%
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{product.source}</span>
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.15)' }}>|</span>
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>⭐ {product.rating}</span>
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.15)' }}>|</span>
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{product.reviews.toLocaleString()} 评价</span>
          </div>
          <div style={{ display: 'flex', gap: 4, marginTop: 8, flexWrap: 'wrap' }}>
            {product.tags.map(tag => (
              <span key={tag} style={{ fontSize: 10, padding: '2px 6px', borderRadius: 6, background: 'rgba(255,255,255,0.04)', color: 'var(--text-muted)' }}>
                {tag}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/** 价格比较行 */
function PriceRowItem({ row, index }: { row: PriceRow; index: number }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1.5fr 1fr 1fr 1fr 0.8fr',
        gap: 12,
        padding: '12px 16px',
        alignItems: 'center',
        borderRadius: index === 0 ? 8 : 0,
        background: row.best ? 'rgba(232, 210, 138, 0.06)' : 'transparent',
        borderBottom: '1px solid rgba(255,255,255,0.04)',
        animation: `fadeUp 0.4s ${(0.1 + index * 0.04).toFixed(2)}s var(--ease) both`,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontSize: 13, color: row.best ? 'var(--accent)' : 'var(--text-secondary)', fontWeight: row.best ? 500 : 400 }}>
          {row.platform}
        </span>
        {row.best && <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 4, background: 'var(--accent)', color: '#030B18', fontWeight: 600 }}>最低</span>}
      </div>
      <span style={{ fontSize: 14, fontWeight: 600, color: row.best ? 'var(--accent)' : 'var(--text-primary)' }}>
        ¥{row.price}
      </span>
      <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{row.shipping}</span>
      <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{row.warranty}</span>
      <a
        href={row.url}
        style={{
          fontSize: 11, color: 'var(--accent)', textAlign: 'center',
          textDecoration: 'none', opacity: row.best ? 1 : 0.6,
          transition: 'opacity 200ms',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
        onMouseLeave={(e) => { e.currentTarget.style.opacity = row.best ? '1' : '0.6'; }}
      >
        查看 →
      </a>
    </div>
  );
}

/** 评价关键词 */
function ReviewKeywordTag({ keyword }: { keyword: ReviewKeyword }) {
  const bgColor =
    keyword.sentiment === 'positive' ? 'rgba(74, 222, 128, 0.08)' :
    keyword.sentiment === 'negative' ? 'rgba(248, 113, 113, 0.08)' :
    'rgba(255,255,255,0.04)';
  const borderColor =
    keyword.sentiment === 'positive' ? 'rgba(74, 222, 128, 0.15)' :
    keyword.sentiment === 'negative' ? 'rgba(248, 113, 113, 0.15)' :
    'rgba(255,255,255,0.06)';
  const textColor =
    keyword.sentiment === 'positive' ? '#4ade80' :
    keyword.sentiment === 'negative' ? '#f87171' :
    'var(--text-secondary)';

  return (
    <span
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '5px 12px', borderRadius: 20,
        background: bgColor,
        border: `1px solid ${borderColor}`,
        color: textColor, fontSize: 12,
      }}
    >
      {keyword.text}
      <span style={{ fontSize: 10, opacity: 0.6 }}>{keyword.count.toLocaleString()}</span>
    </span>
  );
}

/** Agent 工作状态指示器 */
function AgentStatusIndicator({ agentId, status }: { agentId: string; status: 'idle' | 'running' | 'done' }) {
  const agentMap: Record<string, { name: string; icon: string; color: string }> = {
    intent: { name: '意图解析', icon: '🎯', color: '#6366f1' },
    commerce: { name: 'Commerce Agent', icon: '🛒', color: '#f472b6' },
    budget: { name: '预算分析', icon: '💰', color: '#f87171' },
    risk: { name: '风险评估', icon: '🛡️', color: '#ef4444' },
    explain: { name: '决策解释', icon: '📊', color: '#2dd4bf' },
    simulation: { name: '方案仿真', icon: '🔬', color: '#818cf8' },
  };

  const agent = agentMap[agentId];
  if (!agent) return null;

  const statusColor = status === 'done' ? '#4ade80' : status === 'running' ? agent.color : 'rgba(255,255,255,0.15)';
  const statusLabel = status === 'done' ? '✓' : status === 'running' ? '⚡' : '○';
  const statusText = status === 'done' ? '完成' : status === 'running' ? '分析中...' : '等待';

  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '10px 16px', borderRadius: 12,
        background: status === 'running' ? `${agent.color}08` : 'rgba(255,255,255,0.02)',
        border: `1px solid ${status === 'running' ? `${agent.color}25` : 'rgba(255,255,255,0.05)'}`,
        transition: 'all 300ms var(--ease)',
        animation: status === 'running' ? 'gentlePulse 1.5s ease-in-out infinite' : 'none',
      }}
    >
      <span style={{ fontSize: 18 }}>{agent.icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, color: status === 'running' ? agent.color : 'var(--text-secondary)', fontWeight: 500 }}>
          {agent.name}
        </div>
        <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 1 }}>
          {statusText}
        </div>
      </div>
      <span style={{ fontSize: 14, color: statusColor, fontWeight: 600, minWidth: 18, textAlign: 'center' }}>{statusLabel}</span>
    </div>
  );
}

/* ═══════════════════════════════════════════
   主组件
   ═══════════════════════════════════════════ */

export default function Shopping() {
  const navigate = useNavigate();
  const [input, setInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [activeCategory, setActiveCategory] = useState('全部');
  const [agentStates, setAgentStates] = useState<Record<string, 'idle' | 'running' | 'done'>>({});
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [showResults, setShowResults] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  // 滚动到顶部
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // 模拟商品数据根据分类过滤
  const filteredProducts = activeCategory === '全部'
    ? MOCK_PRODUCTS
    : MOCK_PRODUCTS.filter(p => p.category === activeCategory);

  // 模拟 Agent 启动
  const simulateAgentStart = useCallback((agentId: string) => {
    setAgentStates(prev => ({ ...prev, [agentId]: 'running' }));
  }, []);

  const simulateAgentDone = useCallback((agentId: string) => {
    setAgentStates(prev => ({ ...prev, [agentId]: 'done' }));
  }, []);

  // 提交查询
  const handleSubmit = useCallback(async () => {
    if (!input.trim() || isProcessing) return;
    setIsProcessing(true);
    setAgentStates({});
    setShowResults(false);
    setSelectedProduct(null);

    const delay = (ms: number) => new Promise(r => setTimeout(r, ms));

    // Step 1: 意图解析
    simulateAgentStart('intent');
    await delay(500);
    simulateAgentDone('intent');

    // Step 2: Commerce Agent 商品检索
    simulateAgentStart('commerce');
    await delay(800);
    simulateAgentDone('commerce');

    // Step 3: 预算分析
    simulateAgentStart('budget');
    await delay(400);
    simulateAgentDone('budget');

    // Step 4: 风险评估
    simulateAgentStart('risk');
    await delay(500);
    simulateAgentDone('risk');

    // Step 5: 决策解释
    simulateAgentStart('explain');
    await delay(600);
    simulateAgentDone('explain');

    setIsProcessing(false);
    setShowResults(true);

    // 滚动到结果区域
    setTimeout(() => {
      resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  }, [input, isProcessing, simulateAgentStart, simulateAgentDone]);

  // 选择商品查看详情
  const handleSelectProduct = useCallback((product: Product) => {
    setSelectedProduct(product);
    // 滚动到比较表格
    setTimeout(() => {
      document.getElementById('price-compare')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleInputFocus = () => inputRef.current?.focus();

  // 示例输入
  const exampleInputs = [
    '想买5000元左右的机械键盘，程序员用',
    '200元以内送女朋友的生日礼物',
    '性价比高的降噪耳机推荐',
  ];

  return (
    <div
      style={{
        minHeight: '100vh',
        padding: '28px 24px 60px',
        fontFamily: 'var(--font-sans)',
        color: 'var(--text-primary)',
      }}
    >
      {/* ── 页面标题区 ── */}
      <header
        style={{
          maxWidth: 820, margin: '0 auto 28px',
          animation: 'fadeUp 0.6s var(--ease) both',
        }}
      >
        <div style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', letterSpacing: '0.08em', marginBottom: 8 }}>
          {getTimeOfDay()} · Commerce Agent
        </div>
        <h1 style={{ fontSize: 28, fontWeight: 300, letterSpacing: '-0.02em', margin: '0 0 6px' }}>
          购物决策
        </h1>
        <p style={{ fontSize: 14, color: 'var(--text-muted)', margin: 0, lineHeight: 1.6 }}>
          描述你的购物需求，AI Agent 跨平台检索 · 比价 · 分析评价 · 给出最优购买建议
        </p>
      </header>

      {/* ── 分类导航 ── */}
      <nav
        style={{
          maxWidth: 820, margin: '0 auto 24px',
          display: 'flex', gap: 6, flexWrap: 'wrap',
          animation: 'fadeUp 0.6s 0.05s var(--ease) both',
        }}
      >
        {['全部', '数码', '服饰', '家居', '美妆', '食品', '运动'].map((cat) => (
          <button
            key={cat}
            onClick={() => { setActiveCategory(cat); setShowResults(false); }}
            style={{
              padding: '6px 16px', borderRadius: 20,
              background: activeCategory === cat ? 'var(--accent-soft)' : 'rgba(255,255,255,0.03)',
              border: `1px solid ${activeCategory === cat ? 'rgba(232, 210, 138, 0.2)' : 'rgba(255,255,255,0.05)'}`,
              color: activeCategory === cat ? 'var(--accent)' : 'var(--text-muted)',
              fontSize: 13,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              fontWeight: activeCategory === cat ? 500 : 400,
              transition: 'all 200ms',
            }}
          >
            {cat}
          </button>
        ))}
      </nav>

      {/* ── 主内容区 ── */}
      <div style={{ maxWidth: 820, margin: '0 auto' }}>
        {/* 输入框区域 */}
        <div
          style={{
            display: 'flex', flexDirection: 'column', gap: 12,
            animation: 'fadeUp 0.6s 0.1s var(--ease) both',
          }}
        >
          <div
            onClick={handleInputFocus}
            style={{
              display: 'flex', alignItems: 'flex-end', gap: 12,
              padding: '16px 20px', borderRadius: 16,
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.08)',
              transition: 'border-color 200ms',
            }}
          >
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="描述你的购物需求，如：想买5000元左右的机械键盘..."
              disabled={isProcessing}
              rows={2}
              style={{
                flex: 1, background: 'transparent', border: 'none', outline: 'none',
                color: 'var(--text-primary)', fontSize: 14, lineHeight: 1.6,
                resize: 'none', fontFamily: 'var(--font-sans)',
              }}
            />
            <button
              onClick={handleSubmit}
              disabled={!input.trim() || isProcessing}
              style={{
                width: 42, height: 42, borderRadius: 12, border: 'none',
                background: input.trim() ? 'linear-gradient(135deg, #f472b6 0%, #e8d28a 100%)' : 'rgba(255,255,255,0.05)',
                color: '#ffffff', cursor: input.trim() ? 'pointer' : 'default',
                fontSize: 18, opacity: isProcessing ? 0.5 : input.trim() ? 1 : 0.3,
                transition: 'all 200ms', flexShrink: 0,
              }}
            >
              {isProcessing ? '⚡' : '→'}
            </button>
          </div>

          {/* 快捷示例 */}
          {!showResults && !isProcessing && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>试试：</span>
              {exampleInputs.map((example, idx) => (
                <button
                  key={idx}
                  onClick={() => setInput(example)}
                  style={{
                    padding: '4px 10px', borderRadius: 8,
                    background: 'rgba(255,255,255,0.02)',
                    border: '1px solid rgba(255,255,255,0.05)',
                    color: 'var(--text-muted)', fontSize: 11,
                    cursor: 'pointer',
                    transition: 'all 200ms',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(232, 210, 138, 0.15)';
                    e.currentTarget.style.color = 'var(--accent)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.05)';
                    e.currentTarget.style.color = 'var(--text-muted)';
                  }}
                >
                  {example}
                </button>
              ))}
            </div>
          )}

          {/* Agent 工作状态 */}
          {isProcessing && Object.keys(agentStates).length > 0 && (
            <div
              ref={resultsRef}
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
                gap: 8,
                padding: '16px',
                borderRadius: 16,
                background: 'rgba(255,255,255,0.02)',
                border: '1px solid rgba(255,255,255,0.05)',
                animation: 'fadeUp 0.3s var(--ease) both',
              }}
            >
              {Object.entries(agentStates).map(([id, status]) => (
                <AgentStatusIndicator key={id} agentId={id} status={status as 'idle' | 'running' | 'done'} />
              ))}
            </div>
          )}
        </div>

        {/* ── 商品搜索结果 ── */}
        {(showResults || (!isProcessing && activeCategory !== '全部' && !input.trim())) && (
          <div
            ref={resultsRef}
            style={{
              marginTop: 32,
              animation: 'fadeUp 0.5s 0.1s var(--ease) both',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <h2 style={{ fontSize: 16, fontWeight: 400, color: 'var(--text-secondary)', margin: 0 }}>
                {showResults ? '搜索结果' : `${activeCategory}推荐`}
              </h2>
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                共 {filteredProducts.length} 件商品
              </span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {filteredProducts
                .sort((a, b) => b.match - a.match)
                .map((product, idx) => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    index={idx}
                    onSelect={handleSelectProduct}
                  />
                ))}
            </div>
          </div>
        )}

        {/* 未搜索时展示默认商品推荐 */}
        {!isProcessing && !showResults && !input.trim() && activeCategory === '全部' && (
          <div style={{ marginTop: 32, animation: 'fadeUp 0.5s 0.15s var(--ease) both' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <h2 style={{ fontSize: 16, fontWeight: 400, color: 'var(--text-secondary)', margin: 0 }}>
                热门推荐
              </h2>
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>为你精选</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {MOCK_PRODUCTS.filter(p => p.match >= 0.9)
                .sort((a, b) => b.match - a.match)
                .slice(0, 4)
                .map((product, idx) => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    index={idx}
                    onSelect={handleSelectProduct}
                  />
                ))}
            </div>
          </div>
        )}

        {/* ── 价格比较表格 ── */}
        {!isProcessing && showResults && (
          <div
            id="price-compare"
            style={{
              marginTop: 36,
              padding: '24px',
              borderRadius: 16,
              background: 'rgba(255,255,255,0.02)',
              border: '1px solid rgba(255,255,255,0.06)',
              animation: 'fadeUp 0.5s 0.2s var(--ease) both',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <div>
                <h2 style={{ fontSize: 16, fontWeight: 400, color: 'var(--text-primary)', margin: 0 }}>
                  价格对比
                </h2>
                <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '4px 0 0' }}>
                  {selectedProduct ? `「${selectedProduct.name}」跨平台比价` : 'Keychron K8 Pro 红轴 — 5 大平台实时比价'}
                </p>
              </div>
              <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 8, background: 'rgba(232, 210, 138, 0.06)', color: 'var(--accent)' }}>
                最便宜 ¥{Math.min(...MOCK_PRICE_COMPARE.map(r => r.price))}
              </span>
            </div>

            {/* 表格头 */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1.5fr 1fr 1fr 1fr 0.8fr',
                gap: 12,
                padding: '8px 16px',
                marginBottom: 4,
              }}
            >
              {['平台', '价格', '运费', '质保', ''].map(label => (
                <span key={label} style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 500 }}>{label}</span>
              ))}
            </div>
            <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
              {MOCK_PRICE_COMPARE.map((row, idx) => (
                <PriceRowItem key={row.platform} row={row} index={idx} />
              ))}
            </div>
          </div>
        )}

        {/* ── 评价摘要 ── */}
        {!isProcessing && showResults && (
          <div
            style={{
              marginTop: 28,
              padding: '24px',
              borderRadius: 16,
              background: 'rgba(255,255,255,0.02)',
              border: '1px solid rgba(255,255,255,0.06)',
              animation: 'fadeUp 0.5s 0.3s var(--ease) both',
            }}
          >
            <h2 style={{ fontSize: 16, fontWeight: 400, color: 'var(--text-primary)', margin: 0 }}>
              AI 评价摘要
            </h2>
            <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '4px 0 16px' }}>
              基于 {MOCK_PRODUCTS[0].reviews.toLocaleString()} 条真实用户评价分析
            </p>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
              {MOCK_REVIEW_KEYWORDS.map(keyword => (
                <ReviewKeywordTag key={keyword.text} keyword={keyword} />
              ))}
            </div>

            {/* 评价分布 */}
            <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>好评</span>
                <div style={{ width: 100, height: 6, borderRadius: 3, background: 'rgba(255,255,255,0.05)', overflow: 'hidden' }}>
                  <div style={{ width: '82%', height: '100%', borderRadius: 3, background: 'linear-gradient(90deg, #4ade80, #22c55e)' }} />
                </div>
                <span style={{ fontSize: 11, color: '#4ade80' }}>82%</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>中评</span>
                <div style={{ width: 100, height: 6, borderRadius: 3, background: 'rgba(255,255,255,0.05)', overflow: 'hidden' }}>
                  <div style={{ width: '13%', height: '100%', borderRadius: 3, background: 'linear-gradient(90deg, #fbbf24, #f59e0b)' }} />
                </div>
                <span style={{ fontSize: 11, color: '#fbbf24' }}>13%</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>差评</span>
                <div style={{ width: 100, height: 6, borderRadius: 3, background: 'rgba(255,255,255,0.05)', overflow: 'hidden' }}>
                  <div style={{ width: '5%', height: '100%', borderRadius: 3, background: 'linear-gradient(90deg, #f87171, #ef4444)' }} />
                </div>
                <span style={{ fontSize: 11, color: '#f87171' }}>5%</span>
              </div>
            </div>
          </div>
        )}

        {/* ── 购买建议卡片 ── */}
        {!isProcessing && showResults && (
          <div
            style={{
              marginTop: 28,
              padding: '24px',
              borderRadius: 16,
              background: 'linear-gradient(135deg, rgba(232, 210, 138, 0.06) 0%, rgba(232, 210, 138, 0.02) 100%)',
              border: '1px solid rgba(232, 210, 138, 0.15)',
              animation: 'fadeUp 0.5s 0.4s var(--ease) both',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(232, 210, 138, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>
                🛒
              </div>
              <div>
                <h2 style={{ fontSize: 16, fontWeight: 400, color: 'var(--text-primary)', margin: 0 }}>
                  AI 购买建议
                </h2>
                <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '2px 0 0' }}>
                  Commerce Agent · 综合评分 {MOCK_PRODUCTS[0].match * 100}% 推荐
                </p>
              </div>
            </div>

            {/* 推荐商品 */}
            <div
              style={{
                padding: '16px 20px', borderRadius: 12,
                background: 'rgba(232, 210, 138, 0.04)',
                border: '1px solid rgba(232, 210, 138, 0.1)',
                marginBottom: 16,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)' }}>
                  🏆 首选推荐：{MOCK_PRODUCTS[0].name}
                </span>
                <span style={{ fontSize: 18, fontWeight: 600, color: 'var(--accent)' }}>
                  ¥{MOCK_PRODUCTS[0].price}
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {getRecommendationReason(MOCK_PRODUCTS[0]).map((reason, idx) => (
                  <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--accent)', flexShrink: 0 }} />
                    <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{reason}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* 行动按钮 */}
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                style={{
                  flex: 1, padding: '12px 0', borderRadius: 10,
                  background: 'linear-gradient(135deg, #f472b6 0%, #ec4899 100%)',
                  border: 'none', color: '#ffffff', fontSize: 13,
                  fontWeight: 500, cursor: 'pointer',
                  transition: 'all 200ms',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(244, 114, 182, 0.3)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                加入购物车
              </button>
              <button
                style={{
                  padding: '12px 20px', borderRadius: 10,
                  background: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  color: 'var(--text-secondary)', fontSize: 13,
                  cursor: 'pointer', transition: 'all 200ms',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(232, 210, 138, 0.2)'; e.currentTarget.style.color = 'var(--accent)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
              >
                查看替代方案
              </button>
              <button
                onClick={() => navigate('/agents')}
                style={{
                  padding: '12px 20px', borderRadius: 10,
                  background: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  color: 'var(--text-secondary)', fontSize: 13,
                  cursor: 'pointer', transition: 'all 200ms',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(232, 210, 138, 0.2)'; e.currentTarget.style.color = 'var(--accent)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
              >
                深入了解 →
              </button>
            </div>
          </div>
        )}

        {/* ── 底部 Agent 状态栏 ── */}
        <div
          style={{
            marginTop: 48, paddingTop: 32,
            borderTop: '1px solid rgba(255,255,255,0.05)',
            animation: 'fadeUp 0.5s 0.5s var(--ease) both',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#34d399', animation: 'gentlePulse 2s ease-in-out infinite' }} />
            <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
              Commerce Agent 已就绪
            </span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {[
              { icon: '🛒', name: 'Commerce Agent' },
              { icon: '💰', name: '预算分析' },
              { icon: '🛡️', name: '风险评估' },
              { icon: '📊', name: '决策解释' },
            ].map(item => (
              <span
                key={item.name}
                style={{
                  padding: '5px 12px', borderRadius: 20,
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.05)',
                  fontSize: 12, color: 'var(--text-muted)',
                }}
              >
                {item.icon} {item.name}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* ── 全局样式（动画） ── */}
      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes gentlePulse {
          0%, 100% { opacity: 0.5; }
          50% { opacity: 1; }
        }
      `}</style>
    </div>
  );
}
